import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { put } from "redux-saga/effects";
import { applyDiscountSuccess, applyDiscountFail } from "../../slices/DiscountSlice";
import { notifyError } from "../../../effects-bridge";

/**
 * Uplifted from `redux-store/saga/discount` (apps/web 91 lines + apps/mobile 76).
 *
 * A SPLIT. Three things are byte-identical and live here — the private `commonPostData` POST
 * wrapper, the `applyDiscount` worker, and (since 2026-09-08) `getExternalDiscountsList`. The
 * other two workers diverge on THREE axes at once (offline sync, the error UI, and even which
 * endpoint section they read), so `root` still belongs to the platform files — but both now
 * watch the same FOUR actions.
 *
 * ⚠️ Bare folder imports are REQUIRED in a `shared.ts` (hazard 16) — `utils/apiService`,
 * `config/endpoints` and `slices/DiscountSlice` are all platform-split folders and this file runs
 * on both platforms.
 *
 * ⚠️ `pos_errormap` is NOT imported anywhere in this folder. Web's app file imported it and never
 * referenced it — a hazard-13 phantom, re-measured this session (the ledger's session-16 census
 * predicted exactly this). Dropped under rule 2(b).
 */

/**
 * POST helper shared by every worker. Identical in both apps.
 *
 * ⚠️ **Swallows every error and returns `undefined`** — the bare `catch {}` is the apps' own.
 * Each caller then treats a falsy response as failure, which is why the workers below all begin
 * with a `response &&` guard. Preserved verbatim; "fixing" it would change four call paths.
 *
 * Note it is `async`, not a generator: the workers `yield` the promise it returns, which
 * redux-saga resolves. That is the apps' shape too.
 */
export async function commonPostData(url: string, data: any) {
  try {
    const postApi = api.request({
      method: "POST",
      url: url,
      data,
    });
    const fetchResponse = await postApi;
    return fetchResponse;
  } catch (e) {}
}

/**
 * Apply a coupon/discount. Identical in both apps.
 *
 * Only a response with `status === "Active"` counts as success; anything else fires the caller's
 * `errorCallback` AND dispatches the failure action.
 */
export function* applyDiscount(actions: any): Generator<any, void, any> {
  let url = ENDPOINT.DISCOUNT.appplyDiscount();
  try {
    let data = actions.payload;
    let response = yield commonPostData(url, JSON.stringify(data.data));
    if (response && response.status === "Active") {
      yield put(applyDiscountSuccess(response));
    } else {
      data.errorCallback && data.errorCallback();
      yield put(applyDiscountFail(response));
    }
  } catch (e) {
    // ⚠️ `applyDiscountFail` is a NO-PAYLOAD reducer in `DiscountSlice/shared.ts`
    // (`(state) => { state.isDiscountApplying = false; }`), so RTK types its creator as
    // `ActionCreatorWithoutPayload` and this object literal is `TS2345` — hazard 14's
    // type-level trap. BOTH apps pass this payload anyway; the reducer ignores it, but the
    // dispatched action still carries it (redux-logger and devtools see it). The cast keeps
    // the call VERBATIM rather than dropping the argument, which would be a real change to
    // the action object. The `applyDiscountFail(response)` call above needs no cast only
    // because `response` is `any`, which is assignable to `void`.
    yield put((applyDiscountFail as any)({ e, status: false }));
  }
}

/**
 * The partner offers available to this customer against this sale.
 *
 * ⚠️ MOVED here from `index.desktop.ts` on 2026-09-08, body verbatim, when
 * `packages/core/discounts` took the coupon/deals flow to both platforms. Its endpoint
 * (`DISCOUNT.getExternalDiscounts`) and its action came with it. It was web-only because only
 * web had an offers surface — hazard 32's "want of a caller, not a platform half".
 *
 * The offers are handed to the caller's callback and never stored: they are per-sale, so adding
 * an item invalidates them, and a slice would have to be swept.
 */
export function* getExternalDiscountsList(actions: any): Generator<any, void, any> {
  const data = actions.payload?.data;
  const url = ENDPOINT.DISCOUNT.getExternalDiscounts();
  const callBack = actions?.payload?.callback;
  const response = yield commonPostData(url, JSON.stringify(data));
  if (response && !response.code) {
    callBack && callBack(response);
  } else {
    notifyError(response?.code);
  }
}
